import { useState } from 'react'
import { ChevronDown, ChevronRight, Play, Voicemail } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { cn } from '@/lib/utils'
import { useThreadMailbox } from '@/features/communications/mailbox'
import { useCallRecording, usePhoneCallForMessage, type PhoneCall } from '@/features/phone-calls/hooks'

function clock(seconds: number | null | undefined) {
  const total = Math.max(0, Math.floor(seconds ?? 0))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** Play button that turns into the audio player once Quo's recording link is fetched. */
function Recording({ callUuid, voicemail }: { callUuid: string; voicemail?: boolean }) {
  const recording = useCallRecording()
  if (recording.data) {
    return (
      <audio
        controls
        autoPlay
        src={recording.data.url}
        className="h-9 w-full max-w-md"
        aria-label={voicemail ? 'Voicemail recording' : 'Call recording'}
      />
    )
  }
  return (
    <LoadingButton
      size="sm"
      variant="outline"
      className="h-8"
      loading={recording.isPending}
      loadingText="Loading…"
      onClick={() =>
        recording.mutate(
          { callUuid, voicemail },
          { onError: () => toast.error('Could not load the recording') },
        )
      }
    >
      {voicemail ? <Voicemail className="size-3.5" /> : <Play className="size-3.5" />}
      {voicemail ? 'Play voicemail' : 'Play recording'}
    </LoadingButton>
  )
}

function Lines({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1 pl-4 text-sm marker:text-muted-foreground">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}

/** What a call left: Quo's summary and next steps, the recording, and the transcript behind a click. */
export function CallBody({ call, theirName }: { call: PhoneCall; theirName?: string | null }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const summary = call.summary?.summary ?? []
  const nextSteps = call.summary?.next_steps ?? []
  const transcript = call.transcript ?? []
  const them = theirName || call.party_name || 'Caller'
  return (
    <div className="space-y-3">
      {summary.length > 0 && <Lines items={summary} />}
      {nextSteps.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Next steps</p>
          <Lines items={nextSteps} />
        </div>
      )}
      {call.voicemail_transcript && (
        <p className="whitespace-pre-wrap text-sm">{call.voicemail_transcript}</p>
      )}
      {(call.has_recording || call.has_voicemail) && (
        <div className="flex flex-wrap items-center gap-2">
          {call.has_recording && <Recording callUuid={call.uuid} />}
          {call.has_voicemail && <Recording callUuid={call.uuid} voicemail />}
        </div>
      )}
      {transcript.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setTranscriptOpen((open) => !open)}
            aria-expanded={transcriptOpen}
            className="-ml-1 inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {transcriptOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            Transcript
          </button>
          {transcriptOpen && (
            <ol className="mt-2 space-y-2">
              {transcript.map((turn, index) => (
                <li key={index} className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-2 text-sm">
                  <span className="pt-px text-xs tabular-nums text-muted-foreground">{clock(turn.start)}</span>
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'mr-1.5 font-medium',
                        turn.speaker === 'us' ? 'text-sky-700 dark:text-sky-300' : 'text-foreground',
                      )}
                    >
                      {turn.speaker === 'us' ? turn.name || 'Us' : them}
                    </span>
                    {turn.text}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

/** The body of an expanded call message: the call itself, else the text it was filed with. */
export function CallMessageBody({
  messageUuid,
  fallbackText,
  theirName,
}: {
  messageUuid: string | undefined
  fallbackText: string | null
  theirName?: string | null
}) {
  const mailbox = useThreadMailbox()
  const call = usePhoneCallForMessage(messageUuid, mailbox)
  if (call.data) return <CallBody call={call.data} theirName={theirName} />
  if (call.isLoading) return <div className="h-16 animate-pulse rounded-md bg-muted/60" aria-label="Loading call" />
  return <div className="whitespace-pre-wrap text-sm">{fallbackText}</div>
}
